/**
 * Helper functions for EA Report period calculations
 * These functions extract common patterns to improve code readability and maintainability
 */

import { date, formatApiDate } from '~/lib/date';
import { IContractRatePeriod, IEnvironmentalRatePeriod, ProjectOfferPeriod } from '~/models/project';
import { CalculatedProjectOfferPeriod } from './reportTypes';

/**
 * Check if two date ranges overlap
 */
export function doDateRangesOverlap(start1: string, end1: string, start2: string, end2: string): boolean {
  const start1Value = date(start1).valueOf();
  const end1Value = date(end1).valueOf();
  const start2Value = date(start2).valueOf();
  const end2Value = date(end2).valueOf();
  return start1Value <= end2Value && end1Value >= start2Value;
}

/**
 * Find all environmental periods that overlap with a contract period's date range
 */
export function findOverlappingEnvironmentalPeriods(
  contractPeriod: IContractRatePeriod,
  environmentalPeriods: IEnvironmentalRatePeriod[]
): IEnvironmentalRatePeriod[] {
  return environmentalPeriods.filter((envPeriod) =>
    doDateRangesOverlap(contractPeriod.startDate, contractPeriod.endDate, envPeriod.startDate, envPeriod.endDate)
  );
}

/**
 * Collect all boundary dates from a contract period and overlapping environmental periods
 */
export function collectBoundaryDates(
  contractPeriod: IContractRatePeriod,
  overlappingEnvironmentalPeriods: IEnvironmentalRatePeriod[]
): string[] {
  const boundaryDates = new Set<string>();
  boundaryDates.add(contractPeriod.startDate);
  boundaryDates.add(contractPeriod.endDate);

  const contractStart = date(contractPeriod.startDate).valueOf();
  const contractEnd = date(contractPeriod.endDate).valueOf();

  overlappingEnvironmentalPeriods.forEach((envPeriod) => {
    const envStart = date(envPeriod.startDate).valueOf();
    const envEnd = date(envPeriod.endDate).valueOf();

    // Include environmental period boundaries that fall within contract period
    // Exclude boundaries that exactly match contract period boundaries (to avoid creating empty segments)
    // Use strict comparison (< and >) for inner boundaries to only include boundaries that are truly inside
    if (envStart > contractStart && envStart < contractEnd) {
      boundaryDates.add(envPeriod.startDate);
    }
    if (envEnd > contractStart && envEnd < contractEnd) {
      boundaryDates.add(envPeriod.endDate);
    }
  });

  // Sort boundaries
  return Array.from(boundaryDates)
    .map((bd) => date(bd))
    .sort((a, b) => a.valueOf() - b.valueOf())
    .map((d) => formatApiDate(d));
}

/**
 * Create segments by splitting a date range at boundary dates
 */
export function createSegmentsFromBoundaries(boundaries: string[]): Array<{ start: string; end: string }> {
  const segments: Array<{ start: string; end: string }> = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    segments.push({
      start: boundaries[i],
      end: boundaries[i + 1]
    });
  }
  return segments;
}

/**
 * Calculate overlap duration between two date ranges
 * Handles boundary cases: if segment end equals period start (or segment start equals period end),
 * it's considered as a valid match (overlap = 1 day for boundary touching)
 */
function calculateOverlapDuration(start1: string, end1: string, start2: string, end2: string): number {
  const start1Value = date(start1).valueOf();
  const end1Value = date(end1).valueOf();
  const start2Value = date(start2).valueOf();
  const end2Value = date(end2).valueOf();

  // Check if segments touch at boundaries (segment end = period start, or segment start = period end)
  // In this case, consider it as a match with 1 day overlap
  if (end1Value === start2Value || start1Value === end2Value) {
    return 1; // 1 day for boundary touching
  }

  // Normal overlap calculation
  const overlapStart = Math.max(start1Value, start2Value);
  const overlapEnd = Math.min(end1Value, end2Value);

  return overlapEnd > overlapStart ? overlapEnd - overlapStart : 0;
}

/**
 * Find the environmental period that best matches a segment.
 * Logic: 1) Filter by contract periodName (same periodName, e.g. 36M contract → env periodName 36).
 *        2) If multiple matches: pick env period with largest endDate; tiebreaker: same periodName.
 */
export function findBestMatchingEnvironmentalPeriod(
  segment: { start: string; end: string },
  overlappingEnvironmentalPeriods: IEnvironmentalRatePeriod[],
  contractPeriodName?: number
): IEnvironmentalRatePeriod | undefined {
  if (overlappingEnvironmentalPeriods.length === 0) return undefined;

  // Filter by periodName: prefer env periods with same periodName as contract
  const withOverlap = overlappingEnvironmentalPeriods.filter((envPeriod) => {
    const overlap = calculateOverlapDuration(segment.start, segment.end, envPeriod.startDate, envPeriod.endDate);
    return overlap > 0;
  });
  if (withOverlap.length === 0) return undefined;

  // If contractPeriodName provided, filter to same periodName first; fallback to all if none match
  let candidates = withOverlap;
  if (contractPeriodName != null) {
    const samePeriodName = withOverlap.filter((p) => p.periodName === contractPeriodName);
    if (samePeriodName.length > 0) candidates = samePeriodName;
  }

  // Select: largest endDate; if tie, prefer same periodName
  candidates.sort((a, b) => {
    const endA = date(a.endDate).valueOf();
    const endB = date(b.endDate).valueOf();
    if (endB !== endA) return endB - endA; // desc: larger endDate first
    if (contractPeriodName != null) {
      const aMatch = a.periodName === contractPeriodName ? 1 : 0;
      const bMatch = b.periodName === contractPeriodName ? 1 : 0;
      return bMatch - aMatch; // prefer same periodName
    }
    return 0;
  });

  return candidates[0];
}

/**
 * Merge contract rates and environmental rates into a single period
 */
export function mergeContractAndEnvironmentalRates(
  contractPeriod: IContractRatePeriod,
  environmentalPeriod: IEnvironmentalRatePeriod | undefined,
  segment: { start: string; end: string }
): ProjectOfferPeriod {
  return ProjectOfferPeriod.from({
    id: contractPeriod.id,
    periodName: contractPeriod.periodName,
    offerType: contractPeriod.offerType,
    startDate: segment.start,
    endDate: segment.end,
    // Contract rates from contractPeriod
    peak: contractPeriod.peak,
    shoulder: contractPeriod.shoulder,
    offPeak: contractPeriod.offPeak,
    // Environmental rates from environmentalPeriod (if available)
    lret: environmentalPeriod?.lret,
    lretType: environmentalPeriod?.lretType,
    sres: environmentalPeriod?.sres,
    sresType: environmentalPeriod?.sresType,
    ess: environmentalPeriod?.ess,
    essType: environmentalPeriod?.essType,
    veet: environmentalPeriod?.veet,
    veetType: environmentalPeriod?.veetType,
    aeeis: environmentalPeriod?.aeeis,
    aeeisType: environmentalPeriod?.aeeisType,
    pdrs: environmentalPeriod?.pdrs,
    pdrsType: environmentalPeriod?.pdrsType,
    reps: environmentalPeriod?.reps,
    repsType: environmentalPeriod?.repsType
  });
}

/**
 * Match a period with calculated periods by ID.
 * Segment logic: one output period per periodName (group by periodName, range = minStart..maxEnd).
 * Each period has environmentalRatePeriods = segments. Callers can pass
 * offer.periods.map((p) => p.environmentalRatePeriods || []) to get CalculatedProjectOfferPeriod[][].
 */
export function matchPeriodByIdOrDates(
  period: { id?: string; periodName: number; startDate: string; endDate: string },
  calculatedPeriods: CalculatedProjectOfferPeriod[][]
): CalculatedProjectOfferPeriod[] {
  if (period.id) {
    const matched = calculatedPeriods.find((calculatedGroup) => {
      if (calculatedGroup && calculatedGroup.length > 0) {
        return calculatedGroup.some((p) => p.id === period.id);
      }
      return false;
    });
    if (matched) return matched;
  }

  const matched = calculatedPeriods.find((calculatedGroup) => {
    if (calculatedGroup && calculatedGroup.length > 0) {
      const firstPeriod = calculatedGroup[0];
      return (
        firstPeriod.periodName === period.periodName &&
        firstPeriod.startDate === period.startDate &&
        firstPeriod.endDate === period.endDate
      );
    }
    return false;
  });

  return matched || [];
}
